/* eslint-disable max-params */
import { Provider } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { PermissionsService } from '@shared/services/permissions.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, startWith } from 'rxjs';
import {
  BasePermission,
  PermissionGrants,
} from '@shared/interfaces/permissions.interface';
import {
  PermissionProvider,
  PermissionToken,
  RolePermissionProvider,
} from '../types';

function getPermission(
  router: Router,
  permissionsService: PermissionsService,
  permission: BasePermission,
  grants:
    | PermissionGrants.CanEdit
    | PermissionGrants.CanRemove
    | PermissionGrants.CanView,
) {
  return toSignal(
    combineLatest([
      permissionsService.observeUserPermissions([permission], [grants]),
      router.events.pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        startWith(router),
        map((event) => event.url),
      ),
    ]).pipe(
      map(
        ([hasPermission, url]) =>
          hasPermission &&
          !url.includes('internal-roles') &&
          !url.includes('organization-roles'),
      ),
    ),
  );
}

const constructToken = <T>(pToken: PermissionToken<T>): Provider => {
  return {
    provide: pToken.token,
    useValue: pToken.tokenValue,
  };
};

const constructPermissionProvider = (
  pProvider: PermissionProvider,
): Provider => {
  return {
    deps: [
      Router,
      PermissionsService,
      pProvider.basePermissionToken,
      pProvider.grantsToken,
    ],
    provide: pProvider.provider,
    useFactory: getPermission,
  };
};

export const permissionProviders = (
  rProvider: RolePermissionProvider,
): Provider[] => {
  const providers: Provider[] = [];

  for (const p of rProvider.providers) {
    if (p) {
      providers.push(
        constructPermissionProvider({
          basePermissionToken: p.basePermissionToken,
          grantsToken: p.grantsToken,
          provider: p.provider,
        }),
      );
    }
  }

  for (const permission of rProvider.basePermissions) {
    if (permission) {
      providers.push(
        constructToken({
          token: permission.token,
          tokenValue: permission.tokenValue,
        }),
      );
    }
  }

  for (const grant of rProvider.grants) {
    if (grant) {
      providers.push(
        constructToken({
          token: grant.token,
          tokenValue: grant.tokenValue,
        }),
      );
    }
  }
  return [...providers];
};
